import { useEffect, useId, useState } from 'react'
import { Glass } from '@/components/ui/glass'
import { GlassSelect } from '@/components/ui/glass-select'
import { PAGE_SIZE_OPTIONS, clampPageSize, pageJumpMessage } from '@/lib/list-pagination'

/** "Showing x to y of z" line for the table card's footer. */
export function ListSummary({ page, pageSize, totalItems, isBusy = false }: { page: number; pageSize: number; totalItems: number; isBusy?: boolean }) {
  const from = totalItems === 0 ? 0 : (page - 1) * pageSize + 1
  const to = Math.min(page * pageSize, totalItems)
  // The empty state already says there is nothing; "0 to 0 of 0" under it is noise.
  if (totalItems === 0 && !isBusy) return null
  return (
    <p className="border-t border-border px-4 py-2.5 text-sm text-muted-foreground" aria-live="polite">
      {isBusy && totalItems === 0 ? 'Loading…' : `Showing ${from} to ${to} of ${totalItems} results`}
    </p>
  )
}

const pillInput = 'h-9 rounded-full border border-input bg-[rgb(var(--glass-tint)/0.6)] px-3 text-sm focus-visible:outline-2 focus-visible:outline-solid focus-visible:outline-ring max-sm:h-11'

/**
 * Floating Liquid Glass pager. Render it after the table card, not inside it:
 * sticky needs an ancestor without overflow-hidden.
 */
export default function ListPagination({
  page,
  pageSize,
  pageSizeOptions = [...PAGE_SIZE_OPTIONS],
  totalItems,
  isBusy = false,
  onPageChange,
  onPageSizeChange,
}: {
  page: number
  pageSize: number
  pageSizeOptions?: number[]
  totalItems: number
  isBusy?: boolean
  onPageChange: (page: number) => void
  onPageSizeChange: (size: number) => void
}) {
  const [jumpValue, setJumpValue] = useState(String(page))
  const [jumpHint, setJumpHint] = useState<string | null>(null)
  const jumpId = useId()
  const totalPages = totalItems > 0 ? Math.ceil(totalItems / pageSize) : 0

  useEffect(() => {
    setJumpValue(String(page))
  }, [page])

  // Fewer rows than the smallest page size: nothing to page or resize, and the card's empty state says why.
  if (totalItems <= Math.min(...pageSizeOptions) && !isBusy) return null

  function commitJump() {
    const { target, hint } = pageJumpMessage(jumpValue, page, totalPages)
    setJumpHint(hint)
    setJumpValue(String(target))
    if (target !== page) onPageChange(target)
  }

  return (
    <div className="pointer-events-none sticky bottom-4 z-10 mt-3 flex justify-center">
      <Glass
        as="nav"
        radius={26}
        aria-label="Pagination"
        className="pointer-events-auto flex max-w-full flex-wrap items-center justify-center gap-1 px-2 py-1.5"
      >
        <GlassSelect
          up
          value={String(pageSize)}
          disabled={isBusy}
          onChange={(v) => onPageSizeChange(clampPageSize(Number.parseInt(v, 10)))}
          options={pageSizeOptions.map((size) => ({ value: String(size), label: `${size} per page` }))}
          className="w-36"
          triggerClassName="rounded-full border-input bg-[rgb(var(--glass-tint)/0.6)] hover:bg-[var(--glass-hover)] max-sm:h-11"
          aria-label="Rows per page"
        />
        {/* One page: keep the per-page select (the way back from a large page size), drop the arrows. */}
        {totalPages > 1 ? (
          <>
            <button type="button" className="glass-btn" disabled={page <= 1 || isBusy} onClick={() => onPageChange(page - 1)}>
              Previous
            </button>
            <label className="pl-1 text-sm text-muted-foreground" htmlFor={jumpId}>
              Page
            </label>
            <input
              id={jumpId}
              type="number"
              min={1}
              max={totalPages}
              value={jumpValue}
              disabled={isBusy}
              className={`${pillInput} w-16 text-center font-mono tabular-nums`}
              onChange={(e) => {
                setJumpValue(e.target.value)
                setJumpHint(null)
              }}
              onKeyDown={(e) => {
                if (e.key === 'Enter') commitJump()
              }}
            />
            <span className="text-sm text-muted-foreground">of {totalPages}</span>
            <button type="button" className="glass-btn" disabled={isBusy} onClick={commitJump}>
              Go
            </button>
            <button type="button" className="glass-btn" disabled={page >= totalPages || isBusy} onClick={() => onPageChange(page + 1)}>
              Next
            </button>
            {jumpHint ? (
              <span role="status" aria-live="polite" className="basis-full pb-1 text-center text-xs font-semibold text-foreground">
                {jumpHint}
              </span>
            ) : null}
          </>
        ) : null}
      </Glass>
    </div>
  )
}
